import { computed } from 'vue'
import { useStore } from 'vuex'

/**
 * Composable for checking navigation permissions
 * @returns {Object} Functions and utilities for permission checking
 */
export function useNavigationPermissions() {
  const store = useStore()

  /**
   * Get all permissions for the current user
   */
  const userPermissions = computed(() => {
    const permissionGroups = store.state.globalComponents?.permissions?.permissionGroups || {}
    const allPermissions = []

    // Flatten all permission groups into a single array of permission names
    Object.values(permissionGroups).forEach((group) => {
      if (Array.isArray(group)) {
        group.forEach((perm) => {
          if (perm.name) {
            allPermissions.push(perm.name)
          }
        })
      }
    })

    return allPermissions
  })

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission name (e.g., 'member.view')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!permission) {
      return false
    }
    return userPermissions.value.includes(permission)
  }

  /**
   * Check if user has any of the specified permissions
   * @param {Array<string>} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return false
    }
    return permissions.some((permission) => hasPermission(permission))
  }

  /**
   * Check if user has all of the specified permissions
   * @param {Array<string>} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return false
    }
    return permissions.every((permission) => hasPermission(permission))
  }

  /**
   * Get permissions by group
   * @param {string} group - Permission group name (e.g., 'member', 'opportunity')
   * @returns {Array}
   */
  const getPermissionGroup = (group) => {
    return store.state.globalComponents?.permissions?.permissionGroups?.[group] || []
  }

  /**
   * Check if user has any permission in a group with a specific action
   * @param {string} group - Permission group name
   * @param {string} action - Action name (e.g., 'view', 'create', 'update')
   * @returns {boolean}
   */
  const hasGroupPermission = (group, action) => {
    const permissions = getPermissionGroup(group)
    return permissions.some((perm) => perm.name === `${group}.${action}`)
  }

  /**
   * Get user's role
   */
  const userRole = computed(() => store.state.globalComponents.role)

  /**
   * Check if user has any permissions at all
   * Useful for determining if user should see "no permissions" state
   * @returns {boolean}
   */
  const hasAnyPermissionsAtAll = computed(() => {
    const permissionGroups = store.state.globalComponents?.permissions?.permissionGroups || {}
    const allPermissions = Object.values(permissionGroups).flat()
    return allPermissions.length > 0
  })

  return {
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissionGroup,
    hasGroupPermission,
    userRole,
    hasAnyPermissionsAtAll,
  }
}
